import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  House, Package, UsersThree, MapPin, SignOut, ArrowsClockwise, ClockCounterClockwise, HardHat, MagnifyingGlass, Receipt, CheckSquare, TrayArrowDown, Cube, ListChecks, CurrencyDollar, Storefront, Tag, Buildings, Warning, Truck,
} from "@phosphor-icons/react";

// Nav item shape:
//   { to, label, icon, perm?: string (any-of), roles?: string[], showAll?: true, match?: (loc) => bool }
// A tab is visible when:
//   - `showAll` is true, OR
//   - the user has `perm` (via can()), OR
//   - the user's role is in `roles` (belt-and-braces for store-critical tabs).

const NAV = [
  { to: "/", label: "Dashboard", icon: House, showAll: true },
  { to: "/records", label: "All Records", icon: Package, showAll: true, exactMatch: true },
  { to: "/records?status=open", label: "Returns", icon: Receipt, showAll: true, match: (loc) => loc.pathname === "/records" && new URLSearchParams(loc.search).get("status") === "open" },
  { to: "/records?status=returned", label: "Returned Parts", icon: CheckSquare, showAll: true, match: (loc) => loc.pathname === "/records" && new URLSearchParams(loc.search).get("status") === "returned" },
  { to: "/requests", label: "Requests", icon: TrayArrowDown, showAll: true },
  { to: "/return-approvals", label: "Return Approvals", icon: CheckSquare, roles: ["supervisor", "manager", "admin", "storekeeper"] },
  { to: "/faulty-parts", label: "Faulty Parts", icon: Warning, roles: ["supervisor", "manager", "admin", "storekeeper", "finance"] },
  { to: "/packing-lists", label: "Packing Lists", icon: Truck, roles: ["storekeeper", "manager", "supervisor", "admin"] },
  { to: "/delivery-orders", label: "Delivery Orders", icon: Truck, roles: ["storekeeper", "manager", "supervisor", "admin"] },
  { to: "/purchase-orders", label: "Purchase Orders", icon: Storefront, roles: ["manager", "supervisor", "admin"] },
  { to: "/quotations", label: "Quotations", icon: Receipt, roles: ["manager", "supervisor", "admin"] },
  { to: "/lookup", label: "Work Order Lookup", icon: MagnifyingGlass, showAll: true },
  { to: "/inventory", label: "Inventory", icon: Cube, perm: "view_records", roles: ["supervisor", "manager", "admin", "storekeeper"] },
  { to: "/parts", label: "Parts", icon: ListChecks, perm: "add_stock", roles: ["supervisor", "manager", "admin", "storekeeper"] },
  { to: "/technicians", label: "Technicians", icon: HardHat, perm: "manage_users" },
  { to: "/data", label: "Import/Export", icon: ArrowsClockwise, perm: "export_csv", roles: ["supervisor", "manager", "admin", "storekeeper"] },
  { to: "/audit", label: "Audit Log", icon: ClockCounterClockwise, perm: "view_audit" },
  { to: "/finance", label: "Finance", icon: CurrencyDollar, roles: ["finance", "manager"] },
  { to: "/prices", label: "Price List", icon: Tag, roles: ["finance", "manager"] },
  { to: "/suppliers", label: "Suppliers", icon: Storefront, roles: ["manager"] },
  { to: "/customers", label: "Customers", icon: Buildings, roles: ["manager", "supervisor", "finance", "admin"] },
  { to: "/users", label: "Users", icon: UsersThree, perm: "manage_users" },
  { to: "/regions", label: "Regions", icon: MapPin, perm: "manage_master", roles: ["supervisor", "manager", "storekeeper"] },
];

export default function Layout() {
  const { user, logout, can, hasRole } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isEngineer = hasRole("engineer");

  const doLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  const isActive = (item) => {
    if (item.match) return item.match(loc);
    if (item.exactMatch) {
      return loc.pathname === item.to && !new URLSearchParams(loc.search).get("status");
    }
    return loc.pathname === item.to || loc.pathname.startsWith(item.to + "/");
  };

  const visible = NAV.filter((n) => {
    if (n.showAll) return true;
    if (n.perm && can(n.perm)) return true;
    if (n.roles && hasRole(...n.roles)) return true;
    return false;
  });

  return (
    <div className="min-h-screen bg-[#FDFDFD] text-neutral-900 md:flex">
      {/* Mobile top bar — engineers only. Non-engineers keep the sidebar visible on mobile too. */}
      {isEngineer && (
        <header
          className="no-print md:hidden sticky top-0 z-30 flex items-center justify-between border-b border-neutral-200 bg-white px-4 py-3"
          data-testid="mobile-topbar"
        >
          <div className="flex items-center gap-2">
            <img src="/brand/logo.png" alt="FlowFuel" className="h-7 w-auto" />
            <div className="font-display text-sm tracking-tight leading-none">STOCK</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right leading-tight">
              <div className="text-[11px] font-medium truncate max-w-[140px]" data-testid="mobile-user-name">{user.name}</div>
              <div className="inline-block px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider bg-neutral-900 text-neutral-50">
                {user.role}
              </div>
            </div>
            <button
              onClick={doLogout}
              data-testid="mobile-logout-btn"
              aria-label="Sign out"
              className="p-2 border border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white"
            >
              <SignOut size={14} weight="bold" />
            </button>
          </div>
        </header>
      )}

      {/* Sidebar — hidden on mobile for engineers so the simplified dashboard fills the screen */}
      <aside
        className={`no-print w-64 border-r border-neutral-200 bg-white flex-col ${isEngineer ? "hidden md:flex" : "flex"}`}
        data-testid="sidebar"
      >
        <div className="p-6 border-b border-neutral-200">
          <div className="flex items-center gap-3">
            <img
              src="/brand/logo.png"
              alt="FlowFuel"
              className="h-9 w-auto"
              data-testid="sidebar-logo"
            />
            <div>
              <div className="font-display text-sm tracking-tight leading-none">STOCK</div>
              <div className="label-eyebrow mt-1">v1.3</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {visible.map((n) => {
            const active = isActive(n);
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.exactMatch || n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors ${
                  active
                    ? "bg-neutral-900 text-neutral-50"
                    : "text-neutral-700 hover:bg-neutral-100"
                }`}
              >
                <n.icon size={16} weight="bold" />
                <span>{n.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-neutral-200 p-4 space-y-3">
          <div className="space-y-0.5">
            <div className="label-eyebrow">Signed in</div>
            <div className="text-sm font-medium truncate" data-testid="current-user-name">{user.name}</div>
            <div className="text-xs text-neutral-500 truncate font-mono">{user.email}</div>
            <div className="inline-block mt-1 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-neutral-900 text-neutral-50">
              {user.role}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            data-testid="logout-btn"
            onClick={doLogout}
            className="w-full h-9 rounded-none border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-neutral-50"
          >
            <SignOut size={14} weight="bold" className="mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden pb-16 md:pb-0">
        <Outlet />
      </main>

      {/* Mobile bottom nav — engineers only. Two thumb-friendly tabs. */}
      {isEngineer && (
        <nav
          className="no-print md:hidden fixed bottom-0 left-0 right-0 z-30 flex border-t border-neutral-200 bg-white"
          data-testid="mobile-bottom-nav"
        >
          <NavLink
            to="/"
            end
            data-testid="mobile-bnav-dashboard"
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center gap-1 py-3 text-[11px] font-mono uppercase tracking-wider ${
                isActive ? "text-neutral-900 border-t-2 border-neutral-900 -mt-px" : "text-neutral-500"
              }`
            }
          >
            <House size={22} weight="bold" />
            <span>Dashboard</span>
          </NavLink>
          <NavLink
            to="/requests"
            data-testid="mobile-bnav-requests"
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center gap-1 py-3 text-[11px] font-mono uppercase tracking-wider ${
                isActive ? "text-neutral-900 border-t-2 border-neutral-900 -mt-px" : "text-neutral-500"
              }`
            }
          >
            <TrayArrowDown size={22} weight="bold" />
            <span>Requests</span>
          </NavLink>
        </nav>
      )}
    </div>
  );
}
